import React from 'react';
import { cn } from '@/lib/utils';
import { FramedAvatar } from './FramedAvatar';

interface InstructorDisplayProps {
  instructor: {
    id: string;
    name?: string;
    full_name?: string;
    username?: string;
    profile_picture_url?: string;
    belt_rank?: string;
  };
  className?: string;
  showRemoveButton?: boolean;
  onRemove?: () => void;
  size?: 'sm' | 'md' | 'lg';
}

export function InstructorDisplay({ 
  instructor, 
  className = "",
  showRemoveButton = false,
  onRemove,
  size = 'sm'
}: InstructorDisplayProps) {
  const instructorName = instructor.name || instructor.full_name || 'Unknown Instructor';
  
  const initials = instructorName
    ? instructorName
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'UN';

  // Size configurations
  const sizeConfig = {
    sm: { avatarSize: 24, frameSize: { w: 32, h: 36 }, fontSize: 10 },
    md: { avatarSize: 32, frameSize: { w: 40, h: 44 }, fontSize: 12 },
    lg: { avatarSize: 42, frameSize: { w: 60, h: 66 }, fontSize: 16 }
  };

  const config = sizeConfig[size];

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <FramedAvatar
        src={instructor.profile_picture_url}
        alt={instructorName}
        initials={initials}
        beltRank={instructor.belt_rank}
        size={config.avatarSize}
        frameSize={config.frameSize}
        fontSize={config.fontSize}
      />
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-900 truncate">
          {instructorName}
        </span>
        {instructor.username && (
          <span className="text-xs text-gray-500 truncate">
            @{instructor.username}
          </span>
        )}
      </div>
      {showRemoveButton && onRemove && (
        <button
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 p-1"
          title="Remove instructor"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
} 